import { DownloadOutlined, InboxOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Button, Checkbox, Col, Form, Input, message, Modal, Row, Select, Space, Spin, Tabs, Tooltip, Typography, Upload } from "antd";
import { useState } from "react";
import { MAX_CAPACITY_OPTIONS } from "../../utils/constants";
import { downloadToSystem } from "../../utils/download.utils";
import floor from "lodash/floor";

const maxCapacityOptions = MAX_CAPACITY_OPTIONS;

const GenerateBinsModal = ({ onSubmit, setToggleGenerateBins, formData, bins }) => {
  const [form] = Form.useForm();
  const [excelFile, setExcelFile] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [isLoadingSheets, setIsLoadingSheets] = useState(false);

  const { Dragger } = Upload;
  const { Text } = Typography;

  // Function to extract sheet names from Excel file
  const extractSheetNames = async (file) => {
    try {
      setIsLoadingSheets(true);
      const XLSX = await import("https://cdn.sheetjs.com/xlsx-latest/package/xlsx.mjs");
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheets = workbook.SheetNames;
      setSheetNames(sheets);
      setSelectedSheet(sheets[0]); // Default to first sheet
    } catch (error) {
      console.error("Error reading sheet names:", error);
      message.error("Failed to read Excel file structure");
    } finally {
      setIsLoadingSheets(false);
    }
  };

  const excelProps = {
    name: "file",
    accept: ".xlsx",
    multiple: false,
    beforeUpload: (file) => {
      const isExcel = file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      if (!isExcel) {
        message.error("Only .xlsx Excel files are allowed");
        return Upload.LIST_IGNORE;
      }
      setExcelFile(file);
      extractSheetNames(file); // Extract sheet names when file is uploaded
      return false; // Prevent auto upload
    },
    fileList: fileList,
    onChange: ({ fileList }) => {
      setFileList(fileList);
    },
    onRemove: () => {
      setExcelFile(null);
      setFileList([]);
      setSheetNames([]); // Clear sheet names when file is removed
      setSelectedSheet(null); // Clear selected sheet
      return true;
    },
  };

  const onOk = async () => {
    if (excelFile) {
      const formValues = await form.getFieldsValue();
      Object.keys(formValues).forEach((key) => (formValues[key] = formValues[key] ? Number(formValues[key]) : null));
      onSubmit({ excelFile, formValues, formData, selectedSheetName: selectedSheet });
      setExcelFile(null);
      setSheetNames([]);
      setSelectedSheet(null);
      return;
    }
    const formValues = await form.validateFields();
    Object.keys(formValues).forEach((key) => (formValues[key] = formValues[key] ? Number(formValues[key]) : null));
    onSubmit(formValues);
  };

  const onClose = () => {
    form.resetFields();
    setExcelFile(null);
    setSheetNames([]); // ✅ Clear sheet names
    setSelectedSheet(null); // ✅ Clear selected sheet
    setToggleGenerateBins(false);
  };

  const _isOverWriteBins = Form.useWatch("isOverWriteBins", form);

  const rackWidthEquallyDivied = Form.useWatch("rackWidthEquallyDivied", form);

  const hasColumns = Number(form.getFieldValue("columns")) ? true : false;

  const onOkButtonDisabled = () => {
    if (excelFile) return false;
    return bins.length ? (_isOverWriteBins ? false : true) : false;
  };

  const rackWidthEquallyDivided = (e) => {
    if (e.target.checked) {
      const columns = form.getFieldValue("columns");
      let individualBinWidth = Number(formData.rackWidth) / columns;
      individualBinWidth = Math.floor(individualBinWidth, 2);

      form.setFieldsValue({ binWidth: individualBinWidth });
    } else {
      form.setFieldsValue({ binWidth: null });
    }
  };

  const buildNumberValidationRules = ({ name, required = false, min, max }) => {
    const rules = [];

    if (required) {
      rules.push({
        required: true,
        message: `${name} is required`,
      });
    }

    // Only validate when value is present
    rules.push({
      validator: (_, value) => {
        if (value === undefined || value === null || value === "") {
          return Promise.resolve(); // ✅ No required error here
        }

        const numValue = Number(value);

        if (min !== undefined && numValue < min) {
          return Promise.reject(`${name} must be at least ${min}`);
        }

        if (max !== undefined && numValue > max) {
          return Promise.reject(`${name} must be less than or equal to ${max}`);
        }

        return Promise.resolve();
      },
    });

    return rules;
  };

  const getLabel = (label, isRequired) => (
    <>
      {label}
      {isRequired && <span style={{ color: "red", marginLeft: 4 }}>*</span>}
    </>
  );

  const [didTemplateStartDownloading, setDidTemplateStartDownloading] = useState(true);

  const handleDownloadSample = () => {
    setDidTemplateStartDownloading(false);
    const fileUrl = "src/assets/files/auto-bins-excel-template.xlsx";
    downloadToSystem({
      fileUrl,
      fileName: "bins_generation-template.xlsx",
      successMessage: "Template file downloaded successfully",
      errorMessage: "Failed to download template file",
    }).finally(() => {
      setDidTemplateStartDownloading(true);
    });
  };

  return (
    <Modal
      centered
      maskClosable={false}
      width={380}
      cancelText="Cancel"
      okText="Save"
      title="Generate Bins"
      open={true}
      onOk={onOk}
      onCancel={onClose}
      okButtonProps={{ disabled: onOkButtonDisabled() }}
    >
      <Tabs
        defaultActiveKey="excel"
        onChange={(key) => {
          if (key === "manual") {
            // Uncomment this, if you want to clear uploaded file
            // Clear uploaded file when switching to manual
            //   setExcelFile(null);
            //   setFileList([]);
            //   setSheetNames([]); // Clear sheet names
            //   setSelectedSheet(null); // Clear selected sheet
            //   form.resetFields();
          }
        }}
        items={[
          {
            key: "excel",
            label: "Upload Excel",
            children: (
              <>
                <div style={{ marginBottom: 12, textAlign: "center" }}>
                  <Text strong>Upload Excel File to Auto Generate Bins</Text>
                </div>

                {/* ✅ Download Sample Button */}
                <div style={{ textAlign: "center", marginBottom: 10 }}>
                  <Button type="link" icon={<DownloadOutlined />} onClick={handleDownloadSample} style={{ padding: 0 }}>
                    {didTemplateStartDownloading ? "Download Template" : "Downloading..."}
                  </Button>
                </div>

                <Dragger {...excelProps} style={{ borderRadius: 10 }}>
                  {!excelFile || excelFile.status === "removed" || excelFile.status === "error" || excelFile.status === "uploading" || excelFile === null ? (
                    <Space direction="vertical" size="small">
                      <InboxOutlined style={{ fontSize: 42 }} />
                      <Text strong>Drag and drop your file here</Text>
                      <Text type="secondary">or click to browse</Text>
                      <Text type="success">Only .xlsx files allowed</Text>
                    </Space>
                  ) : (
                    <>
                      <InboxOutlined style={{ fontSize: 42 }} />
                      <Text>{excelFile.name}</Text>
                    </>
                  )}
                </Dragger>

                {/* Sheet Selection Dropdown */}
                {excelFile && sheetNames.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <Form form={form} layout="vertical">
                      <Form.Item label="Select Rack (Sheet)" help="Each sheet represents a different rack">
                        <Select value={selectedSheet} onChange={setSelectedSheet} loading={isLoadingSheets} placeholder="Select a sheet">
                          {sheetNames.map((sheetName) => (
                            <Select.Option key={sheetName} value={sheetName}>
                              {sheetName}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Form>
                  </div>
                )}

                {excelFile && (
                  <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          label="Levels"
                          name="levels"
                          rules={buildNumberValidationRules({
                            name: "Levels",
                            required: false,
                            min: 1,
                          })}
                        >
                          <Input type="number" placeholder="Enter levels" min={1} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label="Columns"
                          name="columns"
                          rules={buildNumberValidationRules({
                            name: "Columns",
                            required: false,
                            min: 1,
                          })}
                        >
                          <Input type="number" placeholder="Enter columns" min={1} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                )}
              </>
            ),
          },
          {
            key: "manual",
            label: "Enter Manually",
            children: (
              <Form
                form={form}
                layout="vertical"
                name="generate_bins_form"
                style={{ margin: "10px 0" }}
                initialValues={{
                  maxCapacity: formData.maxCapacity || null,
                  isOverWriteBins: false,
                }}
                autoComplete="off"
              >
                <Form.Item
                  label="Levels"
                  name="levels"
                  rules={buildNumberValidationRules({
                    name: "Levels",
                    required: true,
                    min: 1,
                  })}
                >
                  <Input type="number" placeholder="Enter levels" />
                </Form.Item>

                <Form.Item label="Columns" name="columns" rules={[{ required: true }]}>
                  <Input type="number" placeholder="Enter columns" />
                </Form.Item>

                <Form.Item
                  label="Bin Width"
                  name="binWidth"
                  rules={[
                    { required: true },
                    {
                      validator: (_, value) => {
                        const columns = form.getFieldValue("columns");
                        const isExceed = Number(columns) * value > Number(formData.rackWidth);
                        return isExceed ? Promise.reject("Exceeding Rack Width") : Promise.resolve();
                      },
                    },
                  ]}
                  style={{ marginBottom: 5 }}
                >
                  <Input type="number" placeholder="Enter Width" disabled={rackWidthEquallyDivied} />
                </Form.Item>

                <Form.Item
                  name="rackWidthEquallyDivied"
                  valuePropName="checked"
                  style={{ textAlign: "center", marginBottom: 15 }}
                  onChange={rackWidthEquallyDivided}
                >
                  <Checkbox disabled={!hasColumns}>Equally Distribute Rack Length</Checkbox>
                </Form.Item>

                <Form.Item label="Bin Height" name="binHeight" rules={[{ required: true }]} style={{ marginBottom: 5 }}>
                  <Input type="number" placeholder="Enter Height" />
                </Form.Item>

                <Form.Item label={
                  <span>
                    Max Capacity
                    <Tooltip title="When unset or 0, capacity is unlimited.">
                      <InfoCircleOutlined style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </span>
                } name="maxCapacity"
                  rules={[
                    {
                      pattern: /^\d{0,4}$/,
                      message: "Max 4 digits allowed",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter Capacity"
                    maxLength={4}
                    onKeyPress={(event) => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                  />
                </Form.Item>

                {bins?.length > 0 && (
                  <Form.Item name="isOverWriteBins" valuePropName="checked" style={{ textAlign: "center" }}>
                    <Checkbox>NOTE: This will overwrite existing bins</Checkbox>
                  </Form.Item>
                )}
              </Form>
            ),
          },
        ]}
      />
    </Modal>
  );
};

export default GenerateBinsModal;
